/**
 * PeersCaller - Main orchestrator class for multi-peer video calls
 * Coordinates all components to provide a simple API for WebRTC calls
 */

import { CallSocket } from "./CallSocket";
import { CallParticipant } from "./CallParticipant";
import { CallRecorder } from "./CallRecorder";
import { useCallStore, type CallStore } from "../store";
import { BaseStore } from "./BaseStore";
import type {
  PeersCallerConfig,
  PeersCallerCallbacks,
  CallParticipant as ICallParticipant,
  PeerConnectionConfig,
  RecordingConfig,
  RecordingData,
  CallStatusResponse,
  TranscriptData,
  PeersCallerError,
} from "../types";

export class PeersCaller extends BaseStore<CallStore> {
  private config: PeersCallerConfig;
  private callbacks: PeersCallerCallbacks;
  private socket: CallSocket;
  private localParticipant: CallParticipant | null = null;
  private participants = new Map<string, CallParticipant>();
  private recorder: CallRecorder | null = null;
  private iceServers: RTCIceServer[] = [];
  private isInitialized = false;
  private listenersSetup = false;

  // Store reference handled by BaseStore

  // Store listener references for cleanup
  private eventListeners = new Map<string, (...args: any[]) => void>();

  constructor(config: PeersCallerConfig, callbacks: PeersCallerCallbacks = {}) {
    super(useCallStore);
    this.config = {
      maxParticipants: 4,
      debug: false,
      socketPath: "/apis/video-call",
      mediaConfig: { video: true, audio: true },
      ...config,
    };

    this.callbacks = callbacks;

    // Initialize socket
    this.socket = new CallSocket(
      this.config.socketUrl,
      this.config.token,
      this.config.socketPath
    );

    // Set up default ICE servers
    this.iceServers = this.config.iceServers || [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
    ];

    // Initialize store
    this.state.setConversationId(this.config.conversationId);

    console.log("[PeersCaller] Initialized with config:", {
      conversationId: this.config.conversationId,
      userId: this.config.userId,
      tokenLength: this.config.token.length,
      maxParticipants: this.config.maxParticipants,
    });
  }

  /**
   * Initialize the PeersCaller and establish WebSocket connection
   */
  async initialize(): Promise<void> {
    try {
      console.log("[PeersCaller] Initializing...");

      if (this.isInitialized) {
        console.warn("[PeersCaller] Already initialized");
        return;
      }

      // Connect to signaling server
      await this.socket.connect();

      // Set up socket event listeners
      this.setupSocketListeners();

      // Initialize local participant (but don't join room yet)
      await this.initializeLocalParticipant();

      this.isInitialized = true;
      this.state.setCallStatus("idle");

      console.log(
        "[PeersCaller] Initialization completed successfully (ready to start/join call)"
      );
    } catch (error) {
      console.error("[PeersCaller] Initialization failed:", error);
      this.handleError("NETWORK_ERROR", `Failed to initialize: ${error}`);
      throw error;
    }
  }

  /**
   * Start a new call
   */
  async startCall(mediaConfig = this.config.mediaConfig): Promise<void> {
    try {
      console.log("[PeersCaller] Starting call...");

      if (!this.isInitialized) {
        await this.initialize();
      }

      // Initialize local media if not done yet
      if (this.localParticipant && !this.localParticipant.getCurrentStream()) {
        const constraints = {
          video:
            mediaConfig?.video && typeof mediaConfig.video === "boolean"
              ? mediaConfig.video
              : true,
          audio:
            mediaConfig?.audio && typeof mediaConfig.audio === "boolean"
              ? mediaConfig.audio
              : true,
        };
        await this.localParticipant.initialize(constraints);
      }

      this.state.setIsCalling(true);
      this.state.setCallStatus("connecting");

      // Send start call event to server with acknowledgement callback
      console.log(
        "[PeersCaller] Starting call for conversation:",
        this.config.conversationId
      );

      const response = await this.socket.startCall(
        this.config.conversationId,
        this.config.userId
      );

      console.log("[PeersCaller] Received call.start response:", response);

      if (response.success) {
        // Update call status to connected
        this.state.setCallStatus("connected");

        // Handle existing participants if any
        response.participants.forEach((userId: string) => {
          if (userId !== this.config.userId) {
            this.handleParticipantJoined(userId);
          }
        });

        console.log("[PeersCaller] Call started successfully");
        this.callbacks.onCallStarted?.(response);
      } else {
        // Handle call start failure
        this.state.setIsCalling(false);
        this.state.setCallStatus("idle");

        const errorMessage = "Failed to start call on server";
        this.handleError("SIGNALING_ERROR", errorMessage);
        console.error("[PeersCaller] Call start failed:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("[PeersCaller] Failed to start call:", error);
      this.handleError("MEDIA_ACCESS_DENIED", `Failed to start call: ${error}`);
      throw error;
    }
  }

  /**
   * Join an existing call
   */
  async joinCall(mediaConfig = this.config.mediaConfig): Promise<void> {
    try {
      console.log("[PeersCaller] Joining call...");

      if (!this.isInitialized) {
        await this.initialize();
      }

      // Check if call is active and we can join
      const callStatus = await this.checkCallStatus();

      if (!callStatus.hasActiveCall) {
        throw new Error("No active call found");
      }

      if (!callStatus.canJoin) {
        throw new Error(
          `Cannot join call: ${
            callStatus.status === "full" ? "Call is full" : "Unable to join"
          }`
        );
      }

      // Set connecting status
      this.state.setCallStatus("connecting");

      // Initialize local media
      if (this.localParticipant && !this.localParticipant.getCurrentStream()) {
        const simpleConfig = {
          video:
            typeof mediaConfig?.video === "boolean" ? mediaConfig.video : true,
          audio:
            typeof mediaConfig?.audio === "boolean" ? mediaConfig.audio : true,
        };
        console.log(
          "[PeersCaller] Initializing local media with config:",
          simpleConfig
        );

        await this.localParticipant.initialize(simpleConfig);
      }

      // Send join request to server with acknowledgement callback
      console.log(
        "[PeersCaller] Joining call for conversation:",
        this.config.conversationId
      );

      const response = await this.socket.joinRoom(
        this.config.conversationId,
        this.config.userId
      );

      console.log("[PeersCaller] Received call.join response:", response);

      // Update call status to connected
      this.state.setCallStatus("connected");
      this.state.setIsCalling(true);

      // Handle existing participants if any
      response.participants.forEach((userId: string) => {
        if (userId !== this.config.userId) {
          this.handleParticipantJoined(userId);
        }
      });

      console.log("[PeersCaller] Joined call successfully");
    } catch (error) {
      console.error("[PeersCaller] Failed to join call:", error);
      this.state.setCallStatus("idle");
      this.handleError(
        "INVALID_CONVERSATION_ID",
        `Failed to join call: ${error}`
      );
      throw error;
    }
  }

  /**
   * End the call and cleanup all resources
   * This sends a request to the server to end the call for all participants
   */
  async endCall(): Promise<void> {
    try {
      console.log("[PeersCaller] Ending call...");

      // Stop recording if active
      if (this.recorder && this.state.isRecording) {
        this.stopRecording();
      }

      // Send end call request to server (server will handle notifications)
      try {
        await this.socket.endCallForAll(this.config.conversationId);
        console.log("[PeersCaller] End call request sent to server");
      } catch (error) {
        console.warn(
          "[PeersCaller] Failed to notify server of call end:",
          error
        );
        // Continue with local cleanup even if server notification fails
      }

      // Cleanup all participants locally
      this.cleanupAllParticipants();

      // Update store
      this.state.setIsCalling(false);
      this.state.setCallStatus("idle");
      this.state.clearParticipants();

      console.log("[PeersCaller] Call ended successfully");
    } catch (error) {
      console.error("[PeersCaller] Error ending call:", error);
    }
  }

  /**
   * Leave the call gracefully (with server acknowledgement)
   */
  async leaveCall(): Promise<void> {
    try {
      console.log("[PeersCaller] Leaving call...");

      // Stop recording if active
      if (this.recorder && this.state.isRecording) {
        this.stopRecording();
      }

      // Send leave request to server with acknowledgement callback
      console.log(
        "[PeersCaller] Leaving call for conversation:",
        this.config.conversationId
      );

      const response = await this.socket.leaveCall(
        this.config.conversationId,
        this.config.userId
      );

      console.log("[PeersCaller] Received call.leave response:", response);

      if (response.success) {
        // Cleanup all participants
        this.cleanupAllParticipants();

        // Update store
        this.state.setIsCalling(false);
        this.state.setCallStatus("idle");
        this.state.clearParticipants();

        console.log("[PeersCaller] Left call successfully");
      } else {
        const errorMessage = "Failed to leave call on server";
        this.handleError("SIGNALING_ERROR", errorMessage);
        console.error("[PeersCaller] Call leave failed:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("[PeersCaller] Failed to leave call:", error);
      this.handleError("SIGNALING_ERROR", `Failed to leave call: ${error}`);
      throw error;
    }
  }

  /**
   * Toggle audio for local participant
   */
  toggleAudio(enabled: boolean): void {
    if (this.localParticipant) {
      this.localParticipant.toggleAudio(enabled);

      // Notify other participants
      this.broadcastStateUpdate({ audioOn: enabled });

      console.log(`[PeersCaller] Audio ${enabled ? "enabled" : "disabled"}`);
    }
  }

  /**
   * Toggle video for local participant
   */
  toggleVideo(enabled: boolean): void {
    if (this.localParticipant) {
      this.localParticipant.toggleVideo(enabled);

      // Notify other participants
      this.broadcastStateUpdate({ videoOn: enabled });

      console.log(`[PeersCaller] Video ${enabled ? "enabled" : "disabled"}`);
    }
  }

  /**
   * Start screen sharing
   */
  async startScreenShare(): Promise<void> {
    try {
      if (!this.localParticipant) {
        throw new Error("Local participant not initialized");
      }

      await this.localParticipant.startScreenShare();

      // Notify other participants
      this.broadcastStateUpdate({ screenSharing: true });

      console.log("[PeersCaller] Screen sharing started");
    } catch (error) {
      console.error("[PeersCaller] Failed to start screen share:", error);
      this.handleError(
        "MEDIA_ACCESS_DENIED",
        `Failed to start screen share: ${error}`
      );
      throw error;
    }
  }

  /**
   * Stop screen sharing
   */
  async stopScreenShare(): Promise<void> {
    try {
      if (!this.localParticipant) {
        throw new Error("Local participant not initialized");
      }

      await this.localParticipant.stopScreenShare();

      // Notify other participants
      this.broadcastStateUpdate({ screenSharing: false });

      console.log("[PeersCaller] Screen sharing stopped");
    } catch (error) {
      console.error("[PeersCaller] Failed to stop screen share:", error);
      throw error;
    }
  }

  /**
   * Start recording the call
   */
  async startRecording(
    recordingData: RecordingData,
    config?: RecordingConfig
  ): Promise<void> {
    try {
      console.log("[PeersCaller] Starting recording...");

      if (this.state.isRecording) {
        console.warn("[PeersCaller] Recording already in progress");
        return;
      }

      // Get all streams for recording
      const streams: MediaStream[] = [];

      // Add local stream
      const localStream = this.localParticipant?.getCurrentStream();
      if (localStream) {
        streams.push(localStream);
      }

      // Add remote streams
      for (const participant of this.participants.values()) {
        const remoteStreams = participant.getAllRemoteStreams();
        streams.push(...remoteStreams.values());
      }

      if (streams.length === 0) {
        throw new Error("No streams available for recording");
      }

      // Initialize recorder
      this.recorder = new CallRecorder(
        this.config.conversationId,
        this.config.userId,
        config,
        {
          onRecordingStarted: (recordingData: RecordingData) => {
            this.socket.startRecording(
              this.config.conversationId,
              recordingData.id
            );
          },
          onRecordingChunk: (chunk: Blob, recordingData: RecordingData) => {
            this.socket.sendRecordingChunk(
              this.config.conversationId,
              recordingData.id,
              chunk
            );
          },
          onRecordingStopped: (recordingData: RecordingData) => {
            this.socket.endRecording(
              this.config.conversationId,
              recordingData.id
            );
          },
          onTranscriptReceived: (transcript: TranscriptData) => {
            this.socket.sendTranscript(
              this.config.conversationId,
              transcript.userId,
              transcript.text
            );
          },
          onError: (error: Error) => {
            this.handleError("RECORDING_FAILED", error.message);
          },
        }
      );

      await this.recorder.startRecording(streams, recordingData);
      this.state.setIsRecording(true);

      console.log("[PeersCaller] Recording started successfully");
    } catch (error) {
      console.error("[PeersCaller] Failed to start recording:", error);
      this.handleError(
        "RECORDING_FAILED",
        `Failed to start recording: ${error}`
      );
      throw error;
    }
  }

  /**
   * Stop recording
   */
  async stopRecording(): Promise<void> {
    try {
      console.log("[PeersCaller] Stopping recording...");

      if (!this.recorder || !this.state.isRecording) {
        console.warn("[PeersCaller] No active recording to stop");
        return;
      }

      await this.recorder.stopRecording();
      this.state.setIsRecording(false);

      console.log("[PeersCaller] Recording stopped successfully");
    } catch (error) {
      console.error("[PeersCaller] Failed to stop recording:", error);
      throw error;
    }
  }

  /**
   * Get current call state
   */
  getCallState(): any {
    return this.state;
  }

  /**
   * Get local participant
   */
  getLocalParticipant(): CallParticipant | null {
    return this.localParticipant;
  }

  /**
   * Get all participants
   */
  getParticipants(): Map<string, CallParticipant> {
    return new Map(this.participants);
  }

  /**
   * Get participant by user ID
   */
  getParticipant(userId: string): CallParticipant | undefined {
    return this.participants.get(userId);
  }

  /**
   * Get debug information about the current call state
   */
  getDebugInfo(): any {
    return {
      isInitialized: this.isInitialized,
      localParticipant: this.localParticipant
        ? {
            userId: this.localParticipant.getUserId(),
            hasStream: !!this.localParticipant.getCurrentStream(),
            streamTracks: this.localParticipant.getCurrentStream()
              ? {
                  video: this.localParticipant
                    .getCurrentStream()!
                    .getVideoTracks().length,
                  audio: this.localParticipant
                    .getCurrentStream()!
                    .getAudioTracks().length,
                }
              : null,
            peerConnections: Array.from(
              this.localParticipant.getAllPeerConnections().keys()
            ),
            connectionStatus: this.localParticipant.getConnectionStatus(),
          }
        : null,
      remoteParticipants: Array.from(this.participants.keys()),
      storeState: this.state,
      socketConnected: this.socket.isConnected(),
    };
  }

  /**
   * Cleanup and disconnect
   */
  cleanup(): void {
    console.log("[PeersCaller] Cleaning up...");

    if (this.state.isCalling) {
      this.endCall();
    }

    // Clean up socket listeners first
    this.cleanupSocketListeners();

    this.cleanupAllParticipants();

    if (this.recorder) {
      this.recorder.cleanup();
      this.recorder = null;
    }

    this.socket.disconnect();
    this.state.resetCall();

    this.isInitialized = false;

    console.log("[PeersCaller] Cleanup completed");
  }

  /**
   * Initialize local participant
   */
  private async initializeLocalParticipant(): Promise<void> {
    this.localParticipant = new CallParticipant(
      this.config.userId,
      {
        onStateChanged: () => {
          // CallParticipant now handles store updates internally
          // This callback is just for external notifications
        },
        onSignal: (fromUserId, toUserId, signal) => {
          // Forward WebRTC signaling data through the socket
          this.forwardSignalToServer(fromUserId, toUserId, signal);
        },
        onError: (error) => {
          this.handleError("UNKNOWN_ERROR", error.message);
        },
      },
      true // isLocalParticipant = true
    );

    // Don't initialize media here - wait for startCall/joinCall
    console.log(
      "[PeersCaller] Local participant created (media not initialized yet)"
    );
  }

  /**
   * Setup WebSocket event listeners
   */
  private setupSocketListeners(): void {
    // Prevent duplicate listener setup
    if (this.listenersSetup) {
      console.log("[PeersCaller] Socket listeners already set up, skipping...");
      return;
    }

    // Clean up any existing listeners first
    this.cleanupSocketListeners();

    console.log("[PeersCaller] Setting up socket listeners...");

    // Listen for participant management events
    const participantJoinedListener = (data: any) => {
      this.handleParticipantJoined(data.userId);
    };
    this.socket.on("call.participant.joined", participantJoinedListener);
    this.eventListeners.set(
      "call.participant.joined",
      participantJoinedListener
    );

    const participantLeftListener = (data: any) => {
      this.handleParticipantLeft(data.userId);
    };
    this.socket.on("call.participant.left", participantLeftListener);
    this.eventListeners.set("call.participant.left", participantLeftListener);

    const participantsListener = (data: any) => {
      // Handle initial participants list when joining
      data.participants.forEach((userId: string) => {
        if (userId !== this.config.userId) {
          this.handleParticipantJoined(userId);
        }
      });
    };
    this.socket.on("call.participants", participantsListener);
    this.eventListeners.set("call.participants", participantsListener);

    const callLeftListener = () => {
      // Confirm that we successfully left the call
      this.state.setIsCalling(false);
    };
    this.socket.on("call.left", callLeftListener);
    this.eventListeners.set("call.left", callLeftListener);

    const callErrorListener = (data: any) => {
      console.error("[PeersCaller] Server error:", data);
      this.callbacks.onError?.(data.error as any, data.message);
    };
    this.socket.on("call.error", callErrorListener);
    this.eventListeners.set("call.error", callErrorListener);

    // Listen for WebRTC signaling events
    const offerListener = (data: any) => {
      this.handleOffer(data.from, data.offer);
    };
    this.socket.on("call.offer", offerListener);
    this.eventListeners.set("call.offer", offerListener);

    const answerListener = (data: any) => {
      this.handleAnswer(data.from, data.answer);
    };
    this.socket.on("call.answer", answerListener);
    this.eventListeners.set("call.answer", answerListener);

    const candidateListener = (data: any) => {
      this.handleCandidate(data.from, data.candidate);
    };
    this.socket.on("call.candidate", candidateListener);
    this.eventListeners.set("call.candidate", candidateListener);

    const stateListener = (data: any) => {
      this.handleParticipantStateUpdate(data.from, data.state);
    };
    this.socket.on("call.state", stateListener);
    this.eventListeners.set("call.state", stateListener);

    const callEndedListener = (data: any) => {
      this.handleCallEnded(data);
    };
    this.socket.on("call.ended", callEndedListener);
    this.eventListeners.set("call.ended", callEndedListener);

    // Listen for call status events
    const statusChangedListener = (data: any) => {
      this.handleCallStatusChanged(data);
    };
    this.socket.on("call.status.changed", statusChangedListener);
    this.eventListeners.set("call.status.changed", statusChangedListener);

    // Listen for recording events
    const recordingStartListener = (data: any) => {
      console.log("[PeersCaller] Recording started:", data.recordingId);
      this.state.setIsRecording(true);
    };
    this.socket.on("call.recording.start", recordingStartListener);
    this.eventListeners.set("call.recording.start", recordingStartListener);

    const recordingChunkListener = (data: any) => {
      console.log("[PeersCaller] Recording chunk received:", data);
    };
    this.socket.on("call.recording.chunk.received", recordingChunkListener);
    this.eventListeners.set(
      "call.recording.chunk.received",
      recordingChunkListener
    );

    const recordingEndListener = (data: any) => {
      console.log("[PeersCaller] Recording ended:", data.recordingId);
      this.state.setIsRecording(false);
    };
    this.socket.on("call.recording.end", recordingEndListener);
    this.eventListeners.set("call.recording.end", recordingEndListener);

    // Listen for transcription events
    const transcriptListener = (data: any) => {
      console.log("[PeersCaller] Transcript received:", data);
      // Handle transcript data - could emit to callbacks or store
      // this.callbacks.onTranscriptReceived?.(data);
    };
    this.socket.on("call.transcript", transcriptListener);
    this.eventListeners.set("call.transcript", transcriptListener);

    this.listenersSetup = true;
    console.log("[PeersCaller] Socket listeners set up successfully");
  }

  /**
   * Clean up socket event listeners
   */
  private cleanupSocketListeners(): void {
    if (!this.listenersSetup && this.eventListeners.size === 0) {
      return;
    }

    console.log("[PeersCaller] Cleaning up socket listeners...");

    // Remove all registered listeners
    for (const [event, listener] of this.eventListeners) {
      this.socket.off(event as any, listener as any);
    }

    // Clear the listeners map
    this.eventListeners.clear();
    this.listenersSetup = false;

    console.log("[PeersCaller] Socket listeners cleaned up successfully");
  }

  /**
   * Handle participant joined
   */
  private async handleParticipantJoined(userId: string): Promise<void> {
    try {
      if (userId === this.config.userId || this.participants.has(userId)) {
        return;
      }

      if (this.participants.size >= this.config.maxParticipants! - 1) {
        console.warn("[PeersCaller] Maximum participants reached");
        return;
      }

      console.log(`[PeersCaller] Participant ${userId} joined`);

      const participant = new CallParticipant(
        userId,
        {
          onStreamReceived: (fromUserId, stream) => {
            this.callbacks.onStreamReceived?.(fromUserId, stream);
          },
          onPeerConnected: (remoteUserId) => {
            console.log(
              `[PeersCaller] Peer connection established with ${remoteUserId}`
            );
          },
          onPeerDisconnected: (remoteUserId) => {
            this.handleParticipantLeft(remoteUserId);
          },
          onSignal: (fromUserId, toUserId, signal) => {
            // Forward WebRTC signaling data through the socket
            this.forwardSignalToServer(fromUserId, toUserId, signal);
          },
          onError: (error) => {
            this.handleError("PEER_CONNECTION_FAILED", error.message);
          },
        },
        false // isLocalParticipant = false
      );

      // Add participant to our list (for state management)
      this.participants.set(userId, participant);

      // Create peer connection from LOCAL participant to the remote participant
      if (!this.localParticipant) {
        console.error(
          "[PeersCaller] Cannot create peer connection - local participant not initialized"
        );
        return;
      }

      // Determine initiator based on user ID comparison (consistent across peers)
      const shouldInitiate = this.config.userId < userId; // Lexicographic comparison
      const peerConfig: PeerConnectionConfig = {
        iceServers: this.iceServers,
        initiator: shouldInitiate,
      };

      // Local participant creates connection TO the remote participant
      await this.localParticipant.createPeerConnection(
        userId, // Connect TO the remote participant
        peerConfig,
        shouldInitiate
      );

      this.callbacks.onParticipantJoined?.({
        userId,
        videoOn: true,
        audioOn: true,
        screenSharing: false,
      });
    } catch (error) {
      console.error(
        `[PeersCaller] Failed to handle participant ${userId} joining:`,
        error
      );
    }
  }

  /**
   * Check call status for the conversation
   */
  async checkCallStatus(): Promise<CallStatusResponse> {
    try {
      console.log("[PeersCaller] Checking call status...");

      if (!this.socket.isConnected()) {
        throw new Error("Socket not connected");
      }

      this.state.setCallStatusLoading(true);
      this.state.setCallStatusError(null);

      // Use new async socket method
      const statusData = await this.socket.checkCallStatus(
        this.config.conversationId,
        this.config.userId
      );

      this.state.setCallStatusInfo(statusData);
      this.state.setCallStatusLoading(false);
      console.log("[PeersCaller] Call status received:", statusData);

      return statusData;
    } catch (error) {
      console.error("[PeersCaller] Failed to check call status:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.state.setCallStatusLoading(false);
      this.state.setCallStatusError(errorMessage);
      this.handleError(
        "NETWORK_ERROR",
        `Failed to check call status: ${errorMessage}`
      );
      throw error;
    }
  }

  /**
   * Handle WebRTC offer
   */
  private async handleOffer(
    fromUserId: string,
    offer: RTCSessionDescriptionInit
  ): Promise<void> {
    try {
      // When receiving an offer FROM a remote user, our LOCAL participant handles it
      // The local participant has a peer connection TO the remote user
      if (!this.localParticipant) {
        console.warn(
          `[PeersCaller] Received offer from ${fromUserId} but local participant not initialized`
        );
        return;
      }

      const peerConnection =
        this.localParticipant.getPeerConnection(fromUserId);
      if (peerConnection) {
        console.log(`[PeersCaller] Processing offer from ${fromUserId}`);
        peerConnection.signal({ type: "offer", sdp: offer.sdp });
      } else {
        console.warn(
          `[PeersCaller] No peer connection found for offer from ${fromUserId}`
        );
      }
    } catch (error) {
      console.error(
        `[PeersCaller] Failed to handle offer from ${fromUserId}:`,
        error
      );
    }
  }

  /**
   * Handle WebRTC answer
   */
  private async handleAnswer(
    fromUserId: string,
    answer: RTCSessionDescriptionInit
  ): Promise<void> {
    try {
      // When receiving an answer FROM a remote user, our LOCAL participant handles it
      // The local participant has a peer connection TO the remote user
      if (!this.localParticipant) {
        console.warn(
          `[PeersCaller] Received answer from ${fromUserId} but local participant not initialized`
        );
        return;
      }

      const peerConnection =
        this.localParticipant.getPeerConnection(fromUserId);
      if (peerConnection) {
        console.log(`[PeersCaller] Processing answer from ${fromUserId}`);
        peerConnection.signal({ type: "answer", sdp: answer.sdp });
      } else {
        console.warn(
          `[PeersCaller] No peer connection found for answer from ${fromUserId}`
        );
      }
    } catch (error) {
      console.error(
        `[PeersCaller] Failed to handle answer from ${fromUserId}:`,
        error
      );
    }
  }

  /**
   * Handle ICE candidate
   */
  private async handleCandidate(
    fromUserId: string,
    candidate: RTCIceCandidateInit
  ): Promise<void> {
    try {
      // When receiving a candidate FROM a remote user, our LOCAL participant handles it
      // The local participant has a peer connection TO the remote user
      if (!this.localParticipant) {
        console.warn(
          `[PeersCaller] Received candidate from ${fromUserId} but local participant not initialized`
        );
        return;
      }

      const peerConnection =
        this.localParticipant.getPeerConnection(fromUserId);
      if (peerConnection) {
        console.log(`[PeersCaller] Processing candidate from ${fromUserId}`);
        peerConnection.signal({
          type: "candidate",
          candidate: candidate as any,
        });
      } else {
        console.warn(
          `[PeersCaller] No peer connection found for candidate from ${fromUserId}`
        );
      }
    } catch (error) {
      console.error(
        `[PeersCaller] Failed to handle candidate from ${fromUserId}:`,
        error
      );
    }
  }

  /**
   * Handle participant state update
   */
  private handleParticipantStateUpdate(
    fromUserId: string,
    state: Partial<ICallParticipant>
  ): void {
    this.state.updateParticipant(fromUserId, state);
    this.callbacks.onParticipantStateChanged?.(fromUserId, state);
  }

  /**
   * Handle participant left
   */
  private handleParticipantLeft(userId: string): void {
    console.log(`[PeersCaller] Participant ${userId} left`);

    const participant = this.participants.get(userId);
    if (participant) {
      participant.cleanup();
      this.participants.delete(userId);
    }

    this.state.removeParticipant(userId);
    this.callbacks.onParticipantLeft?.(userId);
  }

  /**
   * Handle call ended notification from server
   */
  private handleCallEnded(data: {
    conversationId: string;
    endedBy: string;
    reason: string;
  }): void {
    console.log(
      `[PeersCaller] Call ended by ${data.endedBy}, reason: ${data.reason}`
    );

    try {
      // Stop recording if active
      if (this.recorder && this.state.isRecording) {
        this.stopRecording();
      }

      // Cleanup all participants
      this.cleanupAllParticipants();

      // Update store state
      this.state.setIsCalling(false);
      this.state.setCallStatus("idle");
      this.state.clearParticipants();

      // Notify callback about call ending
      this.callbacks.onCallEnded?.(data);

      console.log("[PeersCaller] Call ended cleanup completed");
    } catch (error) {
      console.error("[PeersCaller] Error handling call ended:", error);
    }
  }

  /**
   * Broadcast state update to all participants
   */
  private broadcastStateUpdate(state: Partial<ICallParticipant>): void {
    for (const userId of this.participants.keys()) {
      this.socket.sendStateUpdate(
        userId,
        this.config.userId,
        state,
        this.config.conversationId
      );
    }
  }

  /**
   * Handle call status change
   */
  private handleCallStatusChanged(statusData: CallStatusResponse): void {
    console.log("[PeersCaller] Call status changed:", statusData);

    // Update store with new status
    this.state.setCallStatusInfo(statusData);

    // Notify callback if provided
    this.callbacks.onCallStatusChanged?.(statusData);
  }

  /**
   * Cleanup all participants
   */
  private cleanupAllParticipants(): void {
    for (const participant of this.participants.values()) {
      participant.cleanup();
    }
    this.participants.clear();

    if (this.localParticipant) {
      this.localParticipant.cleanup();
      this.localParticipant = null;
    }
  }

  /**
   * Handle errors with proper categorization
   */
  private handleError(type: PeersCallerError, message: string): void {
    console.error(`[PeersCaller] ${type}: ${message}`);
    this.state.setError(type, message);
    this.callbacks.onError?.(type, message);
  }

  /**
   * Forward WebRTC signaling data to the server
   */
  private forwardSignalToServer(
    fromUserId: string,
    toUserId: string,
    signal: any
  ): void {
    try {
      console.log(
        `[PeersCaller] Forwarding signal from ${fromUserId} to ${toUserId}:`,
        signal.type
      );

      // Determine the signal type and send appropriate socket event
      switch (signal.type) {
        case "offer":
          this.socket.sendOffer(
            toUserId,
            fromUserId,
            signal,
            this.config.conversationId
          );
          break;
        case "answer":
          this.socket.sendAnswer(
            toUserId,
            fromUserId,
            signal,
            this.config.conversationId
          );
          break;
        case "candidate":
          this.socket.sendCandidate(
            toUserId,
            fromUserId,
            signal.candidate,
            this.config.conversationId
          );
          break;
        default:
          console.warn(`[PeersCaller] Unknown signal type: ${signal.type}`);
      }
    } catch (error) {
      console.error(
        `[PeersCaller] Failed to forward signal from ${fromUserId} to ${toUserId}:`,
        error
      );
      this.handleError("SIGNALING_ERROR", `Failed to forward signal: ${error}`);
    }
  }
}
